import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SwPush } from '@angular/service-worker';
import {
  PushSubscriptionRequest,
  PushUnsubscribeRequest,
  PushSubscriptionResponse,
  VapidPublicKeyViewModel,
} from './push-notification.models';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export const PUSH_NOTIFICATION_CONFIG = 'push_notification_config';

export type PushNotificationConfig = {
  apiUrl: string;
};

@Injectable()
export class PushNotificationService {
  private vapidPublicKey: string | null = null;

  constructor(
    private http: HttpClient,
    private swPush: SwPush,
    @Inject(PUSH_NOTIFICATION_CONFIG) private config: PushNotificationConfig
  ) {}

  get isSupported(): boolean {
    return this.swPush.isEnabled && 'Notification' in window;
  }

  get permission(): NotificationPermission {
    return Notification.permission;
  }

  requestPermissionAndSubscribe(): Observable<PushSubscriptionResponse> {
    if (!this.isSupported) {
      return throwError(
        () => new Error('Push notifications are not supported')
      );
    }

    if (this.permission === 'denied') {
      return throwError(() => new Error('Notification permission was denied'));
    }

    return this.getVapidPublicKey().pipe(
      switchMap(() => from(Notification.requestPermission())),
      switchMap((permission) => {
        if (permission !== 'granted') {
          throw new Error('Notification permission not granted');
        }

        return from(this.swPush.subscription);
      }),
      switchMap((existingSubscription) => {
        if (existingSubscription) {
          return this.syncSubscriptionWithServer(existingSubscription);
        }

        return from(
          this.swPush.requestSubscription({
            serverPublicKey: this.vapidPublicKey!,
          })
        ).pipe(
          switchMap((newSubscription) =>
            this.syncSubscriptionWithServer(newSubscription)
          )
        );
      }),
      catchError((error) => {
        console.error('Error subscribing to push notifications:', error);
        return throwError(() => error);
      })
    );
  }

  unsubscribe(): Observable<PushSubscriptionResponse> {
    return from(this.swPush.subscription).pipe(
      switchMap((subscription) => {
        if (!subscription) {
          return of({ success: true, message: 'No active subscription' });
        }

        const subJson = subscription.toJSON();

        if (!subJson.endpoint) {
          throw new Error('Invalid subscription endpoint');
        }

        const request: PushUnsubscribeRequest = {
          endpoint: subJson.endpoint,
        };

        return this.http
          .post<PushSubscriptionResponse>(
            `${this.config.apiUrl}/unsubscribe`,
            request
          )
          .pipe(
            switchMap((response) =>
              from(subscription.unsubscribe()).pipe(map(() => response))
            )
          );
      }),
      catchError((error) => {
        console.error('Error unsubscribing from push notifications:', error);
        return throwError(() => error);
      })
    );
  }

  private syncSubscriptionWithServer(
    subscription: PushSubscription
  ): Observable<PushSubscriptionResponse> {
    const subJson = subscription.toJSON();

    if (!subJson.endpoint || !subJson.keys) {
      throw new Error('Invalid push subscription');
    }

    const request: PushSubscriptionRequest = {
      endpoint: subJson.endpoint,
      keys: {
        p256dh: subJson.keys['p256dh'] || '',
        auth: subJson.keys['auth'] || '',
      },
      deviceId: this.getDeviceId(),
    };

    return this.http.post<PushSubscriptionResponse>(
      `${this.config.apiUrl}/subscribe`,
      request
    );
  }

  isSubscribed(): Observable<boolean> {
    return from(this.swPush.subscription).pipe(
      map((subscription) => !!subscription)
    );
  }

  get notificationMessages(): Observable<any> {
    return this.swPush.messages;
  }

  get notificationClicks(): Observable<any> {
    return this.swPush.notificationClicks;
  }

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('device_id');

    if (!deviceId) {
      deviceId = this.generateDeviceId();
      localStorage.setItem('device_id', deviceId);
    }

    return deviceId;
  }

  private generateDeviceId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }

  private getVapidPublicKey(): Observable<string> {
    if (this.vapidPublicKey) {
      return of(this.vapidPublicKey);
    }

    return this.http
      .get<VapidPublicKeyViewModel>(`${this.config.apiUrl}/vapid-public-key`)
      .pipe(
        map((response) => {
          this.vapidPublicKey = response.publicKey;
          return this.vapidPublicKey;
        }),
        catchError((error) => {
          console.error('Error fetching VAPID public key:', error);
          return throwError(() => error);
        })
      );
  }
}
